import { useEffect, useState } from 'react';
import { CustomCellRendererProps } from 'ag-grid-react';
import { IRowNode } from 'ag-grid-community';
import { Box } from '@mui/material';

type Props = CustomCellRendererProps & { onSelectAll(isSelectAll: boolean): void; isSelectAll: boolean };

export const InfiniteScrollingGridSelectAllHeader = ({ api, onSelectAll, isSelectAll: initialSelectAll }: Props) => {
  // This cannot be managed in the parent as this component doesn't rerender
  const [isSelectAll, setIsSelectAll] = useState(initialSelectAll);

  const handleGetAllRows = () => {
    const rows: IRowNode[] = [];

    api.forEachNode((row) => rows.push(row));

    return rows;
  };

  const handleSelectionChanged = () => {
    const rows = handleGetAllRows();

    setIsSelectAll(rows.length === api.getSelectedRows().length);
  };

  const handleSelectDeselect = (currentSelectAll: boolean) => {
    const rows = handleGetAllRows();

    api.setNodesSelected({
      nodes: rows,
      newValue: currentSelectAll,
    });
  };

  const handleSelectAll = () => {
    setIsSelectAll((prevIsSelectAll) => !prevIsSelectAll);
    handleSelectDeselect(!isSelectAll);
  };

  const handleModelUpdated = () => {
    handleSelectDeselect(isSelectAll);
  };

  const handleRowsDeleted = (event: CustomEvent<{ nodes: IRowNode[] }>) => {
    // Cannot use virtual row removed listener here as it gets triggered recursively and doesn't deselect
    const rows = handleGetAllRows();
    api.setNodesSelected({ nodes: event.detail.nodes, newValue: false });
    api.setRowCount(rows.length - 1);
  };

  useEffect(() => {
    onSelectAll(isSelectAll);
  }, [isSelectAll]);

  useEffect(() => {
    api.addEventListener('selectionChanged', handleSelectionChanged);
    window.addEventListener('rowsDeleted', handleRowsDeleted);
    api.addEventListener('modelUpdated', handleModelUpdated);
    return () => {
      api.removeEventListener('modelUpdated', handleModelUpdated);
      api.removeEventListener('selectionChanged', handleSelectionChanged);
      window.removeEventListener('rowsDeleted', handleRowsDeleted);
    };
  }, [isSelectAll]);

  return (
    <Box className="ag-selection-checkbox" role="presentation">
      <Box role="presentation" className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
        <Box
          className="ag-input-field-label ag-label ag-hidden ag-checkbox-label"
          aria-hidden="true"
          role="presentation"
        />
        <Box
          className={`ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper${isSelectAll ? ' ag-checked' : ''}`}
          role="presentation"
        >
          <input
            className="ag-input-field-input ag-checkbox-input"
            type="checkbox"
            tabIndex={-1}
            onClick={handleSelectAll}
          />
        </Box>
      </Box>
    </Box>
  );
};
